import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchModules } from './modulesConfig';
import ModuleGrid from './ModuleGrid';
import FiscalMapView from './FiscalMapView';
import API_CONFIG from '../../config/api';
import { apiFetch } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';
import { useAuth } from '../../contexts/AuthContext';
import './entryPanel.css';

function isPrestadorAtivo(p) {
  if (!p) return false;
  if (p.inativo === 1 || p.inativo === true || p.inativo === '1') return false;
  if (p.finalizado === 1 || p.finalizado === true || p.finalizado === '1') return false;
  return true;
}

/**
 * Painel de Entrada (/home): grade de módulos + mapa de fiscais (fluxo Contratos).
 * Não altera o módulo de Contratos — só navega para rotas já existentes.
 */
export default function EntryPanelPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { unidadeSelecionada } = useAuth();

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [view, setView] = useState(location.state?.openFiscalMap ? 'map' : 'hub'); // hub | map
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [fiscaisMap, setFiscaisMap] = useState([]);
  const [hubStats, setHubStats] = useState(null);

  const loadModules = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const list = await fetchModules(API_CONFIG.BACKEND_URL);
      setModules(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('EntryPanel: erro ao carregar módulos', e);
      setError(e.message || 'Erro ao carregar módulos.');
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMapData = useCallback(async () => {
    setMapError(null);
    setMapLoading(true);
    try {
      const [resF, resP] = await Promise.all([
        apiFetch(withUnidade('/api/fiscais', unidadeSelecionada)),
        apiFetch(withUnidade('/api/prestadores', unidadeSelecionada)),
      ]);
      if (!resF.ok) throw new Error('Erro ao buscar fiscais');
      const fiscais = await resF.json();
      let prestadores = [];
      if (resP.ok) {
        prestadores = await resP.json();
      }
      const ativos = (Array.isArray(prestadores) ? prestadores : []).filter(isPrestadorAtivo);
      const countBySetor = {};
      ativos.forEach((p) => {
        const s = String(p.setor || '').trim();
        if (!s) return;
        countBySetor[s] = (countBySetor[s] || 0) + 1;
      });

      const list = (Array.isArray(fiscais) ? fiscais : []).map((f) => {
        const setor = String(f.setor || '').trim();
        return {
          id: f.id,
          nome: f.nome || 'Sem nome',
          setor: setor || null,
          email: f.email || null,
          unidade: f.unidade || null,
          qtdContratos: setor ? countBySetor[setor] || 0 : 0,
        };
      });

      setFiscaisMap(list);
      setHubStats({
        fiscais: list.length,
        contratos: ativos.length,
      });
    } catch (e) {
      console.error('EntryPanel: erro ao carregar mapa', e);
      setMapError(e.message || 'Erro ao carregar mapa de fiscais.');
      setFiscaisMap([]);
    } finally {
      setMapLoading(false);
    }
  }, [unidadeSelecionada]);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  useEffect(() => {
    if (location.state?.openFiscalMap) {
      setView('map');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openFiscalMap, location.pathname, navigate]);

  useEffect(() => {
    // KPIs leves no card Contratos (sem abrir o mapa)
    loadMapData();
  }, [loadMapData]);

  const openMap = () => {
    setView('map');
    loadMapData();
  };

  const contratosPath =
    modules.find((m) => m.id === 'contratos')?.path || '/';

  return (
    <div className="entry-panel">
      <div className="entry-panel__net" aria-hidden="true" />

      <div className="entry-panel__content">
        {view === 'hub' && (
          <>
            <header className="entry-panel__header">
              <h1 className="entry-panel__title">Selecione um módulo</h1>
              <p className="entry-panel__subtitle">
                Contratos abre o mapa de fiscais. Demais módulos entram direto na área correspondente.
              </p>
            </header>

            {error && (
              <div className="entry-panel__state">
                <p className="entry-panel__state-title">Erro</p>
                <p className="entry-panel__state-message">{error}</p>
                <button type="button" className="entry-panel__state-button" onClick={loadModules}>
                  Tentar novamente
                </button>
              </div>
            )}

            {!error && !loading && modules.length === 0 && (
              <div className="entry-panel__state">
                <p className="entry-panel__state-title">Nenhum módulo disponível</p>
                <p className="entry-panel__state-message">
                  Não há módulos disponíveis para seu usuário no momento.
                </p>
              </div>
            )}

            {!error && (loading || modules.length > 0) && (
              <ModuleGrid
                modules={modules}
                loading={loading}
                skeletonCount={6}
                stats={hubStats}
                onOpenMap={openMap}
                onNavigate={(path) => path && navigate(path)}
              />
            )}
          </>
        )}

        {view === 'map' && (
          <FiscalMapView
            fiscais={fiscaisMap}
            loading={mapLoading}
            error={mapError}
            onBack={() => setView('hub')}
            onOpenContratos={() => navigate(contratosPath)}
            onOpenFiscal={(f) => f?.id != null && navigate(`/modulos/fiscal/${f.id}/dashboard`)}
            onNovoFiscal={() => navigate('/fiscais')}
            onRetry={loadMapData}
          />
        )}
      </div>
    </div>
  );
}
